'use client';

import { useState, useEffect, useRef } from 'react';
import { fetchAPI } from '@/core/api/client';
import Link from '@/core/i18n/navigation';
import { Skeleton } from '@/core/components/ui/skeleton';

interface CategoryListProps {
    settings?: {
        columns?: number; // grid columns on desktop; 0/undefined = original pill/flex layout
        cardBg?: string;
        cardBorderColor?: string;
        cardBorderRadius?: number;
        textColor?: string;
        textSize?: number;
        gap?: number;
    };
    /** Server-prefetched categories — skips the initial client fetch (and loading flash) on mount. */
    initialCategories?: any[];
    styles?: any;
    viewport?: 'desktop' | 'tablet' | 'mobile';
}

export function CategoryList({ settings: rawSettings = {}, initialCategories, styles, viewport }: CategoryListProps) {
    // Sizing fields (columns/gap/cardBorderRadius/textSize) are responsive — merged over the
    // flat settings here so every existing settings.X read below picks up the per-device value.
    // `columns` in particular had no mobile fallback at all before this (applied the same grid
    // column count via inline style regardless of screen width, unlike the "(Desktop)"-labeled
    // column pickers elsewhere in this codebase that use real CSS breakpoints for mobile).
    const currentStyles = { ...(styles || {}), ...(viewport ? (styles || {})[viewport] || {} : {}) };
    const settings = { ...rawSettings, ...currentStyles };
    const [categories, setCategories] = useState<any[]>(initialCategories || []);
    const [loading, setLoading] = useState(!initialCategories);
    const skipNextFetchRef = useRef(!!initialCategories);

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }
        const loadCategories = async () => {
            const data = await fetchAPI('/categories');
            if (data) setCategories(data);
            setLoading(false);
        };
        loadCategories();
    }, []);

    const gap = settings.gap ?? 16;
    const columns = settings.columns;
    const hasCustomStyle = settings.cardBg !== undefined || settings.cardBorderColor !== undefined || settings.textColor !== undefined;

    if (loading) {
        return (
            <div className="flex flex-wrap" style={{ gap }}>
                {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-10 w-32 rounded-full" />
                ))}
            </div>
        );
    }

    const wrapperClass = columns
        ? 'grid'
        : 'flex flex-wrap';
    const wrapperStyle: React.CSSProperties = columns
        ? { gap, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
        : { gap };

    const linkStyle: React.CSSProperties = hasCustomStyle
        ? {
            backgroundColor: settings.cardBg ?? 'transparent',
            borderColor: settings.cardBorderColor ?? 'transparent',
            borderWidth: settings.cardBorderColor ? 1 : 0,
            borderStyle: 'solid',
            borderRadius: settings.cardBorderRadius !== undefined ? `${settings.cardBorderRadius}px` : 9999,
            color: settings.textColor,
            fontSize: settings.textSize ? `${settings.textSize}px` : undefined,
        }
        : {};

    return (
        <div className={wrapperClass} style={wrapperStyle}>
            {categories.map((category) => (
                <Link
                    key={category.id}
                    href={`/produkty?kategoria=${category.id}`}
                    className={
                        // min-h, not a fixed h-10: a longer name ("Pranie na wagę") used to break into
                        // three lines inside a 40px pill and spill out of it. Pills keep one line and
                        // wrap to the next row; grid tiles grow and centre their text instead.
                        (hasCustomStyle
                            ? 'min-h-10 py-2 px-6 flex items-center justify-center text-center font-bold transition-all hover:opacity-80'
                            : 'min-h-10 py-2 px-6 border border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-primary text-primary text-sm font-bold flex items-center justify-center text-center rounded-full transition-all')
                        + (columns ? '' : ' whitespace-nowrap')
                    }
                    style={linkStyle}
                >
                    {category.name}
                </Link>
            ))}
        </div>
    );
}
